import React, {
  ChangeEvent,
  useCallback,
  useContext,
  useEffect,
  useState,
} from 'react';
import Button from 'kingpin/atoms/Button';
import styled from 'styled-components';
import _ from 'lodash';

import Row from '../../Common/Row';
import DragAndDropList from 'components/DragAndDropList';
import DragHandle from 'components/DragAndDropList/DragHandle';
import V5GadgetFormContext from '../../../contexts/V5GadgetFormContext';
import MetricOptionsContext from '../../../contexts/MetricOptionsContext';
import Inputs from 'components/Inputs';
import Label from '../Label';
import toSentenceCase from '../../../services/toSentenceCase';
import Typography from 'kingpin/atoms/Typography';
import usePopup from '../../../hooks/usePopup';
import MetricPickerPopup from '../../Inputs/MetricPicker/MetricPickerPopup';
import AnalyticsContext from '../../../contexts/AnalyticsContext';
import SingleUseMetricBuilderPopup from '../../Inputs/MetricPicker/MetricPickerPopup/SingleUseMetricBuilderPopup';
import PermissionGates from '../../PermissionGates';
import PERMISSIONS from '../../../permissionDefinitions';
import YAxisConfig from './YAxisConfig';
import { hasQueryOverrides } from '../../V5Gadget/seriesDataKey';
import describeQueryOverrides from '../../../hooks/useV5ChartData/queryOverrides/describeQueryOverrides';
import { useCanOpenStudio } from '../../../contextProviders/SplashScreenProviders/UserAndAccountProviders/UIVersionProvider';
import ThresholdColourInput from 'components/ThresholdColourInput';

const OrderItem = styled.div`
  display: flex;
  flex-direction: row;
  width: 100%;
  justify-content: space-between;
  background-color: var(--color-bg-primary);
  align-items: center;
  margin-bottom: 8px;
  height: 32px;
  padding-right: 8px;
  border: 1px solid var(--color-grey-7);
`;

const ItemWrapper = styled.div`
  margin-bottom: 16px;
  background-color: var(--color-bg-muted);
  padding: 12px;
`;

const InputWrapper = styled.div`
  margin-bottom: 8px;
`;

const Item = ({
  metricIdsToConstrainBy,
  series,
  updateSeries,
  removeSeries,
  isMultipleYAxisChart,
  numSeries,
}: {
  metricIdsToConstrainBy: string[];
  series: V5ChartDefinitionSeries;
  updateSeries: (s: V5ChartDefinitionSeries) => void;
  removeSeries: (s: V5ChartDefinitionSeries) => void;
  isMultipleYAxisChart: boolean;
  numSeries: number;
}) => {
  const { draftChart } = useContext(V5GadgetFormContext);
  const isMatrix = draftChart && draftChart.gadgetType === 'matrix';
  // The "Show comparison value" column only makes sense for a 1D (grouped)
  // matrix. In a 2D date trend the previous value is already the adjacent
  // bucket column, and 2D day/month/range + reversed axes build no comparison
  // data at all - so we hide the toggle rather than show a no-op control.
  const is1DMatrix =
    isMatrix && !draftChart?.dimensionB && !draftChart?.reverseAxes;
  // Threshold colours render on the plain value cell. Reversed axes put
  // metrics on rows, and status flags use their own renderer.
  const isCellColourAvailable =
    isMatrix && !draftChart?.reverseAxes && !series.isStatusFlag;
  const isSmartCard = draftChart && draftChart.gadgetType === 'smartCard';
  const { metricOptions } = useContext(MetricOptionsContext);
  // A series' own query is only edited in Studio
  const canOpenStudio = useCanOpenStudio();
  const [displayName, setDisplayName] = useState<string>(() => {
    if (series.displayName) {
      return series.displayName;
    }
    const selected = metricOptions.find((m) => m.id === series.metricId);
    if (selected) {
      return selected.name;
    }
    return '';
  });

  const isBar = draftChart?.gadgetType === 'bar';
  const isTimeSeries = draftChart?.gadgetType === 'timeSeries';
  const isCombo = draftChart?.gadgetType === 'combo';

  const onPlotOnSeparateYAxisChange = (newValue: boolean) => {
    updateSeries({ ...series, plotOnSeparateAxis: newValue });
  };
  const onOppositeChange = (newValue: boolean) => {
    updateSeries({ ...series, opposite: newValue });
  };
  const onPlotAverageChange = (newValue: boolean) => {
    updateSeries({ ...series, plotAverage: newValue });
  };
  const onPlotTrendChange = (newValue: boolean) => {
    updateSeries({ ...series, plotTrend: newValue });
  };
  const onTypeChange = (newType: string) => {
    updateSeries({ ...series, type: newType });
  };
  const onIsStatusFlagChanged = (newValue: boolean) => {
    updateSeries({ ...series, isStatusFlag: newValue });
  };
  // "Show comparison value" only applies when a cell comparison (delta /
  // percentDelta / both) is selected - the column shows the value the delta is
  // measured against.
  const hasCellComparison =
    !!series.matrixCellType && series.matrixCellType !== 'none';
  const onMatrixCellTypeChanged = (newType?: MatrixCellType) => {
    const isNone = !newType || newType === 'none';
    updateSeries({
      ...series,
      matrixCellType: newType,
      // drop the comparison-value column when comparison is turned off
      matrixShowComparisonValue: isNone
        ? undefined
        : series.matrixShowComparisonValue,
    });
  };
  const onMatrixShowComparisonValueChanged = (newValue: boolean) => {
    updateSeries({ ...series, matrixShowComparisonValue: newValue });
  };
  const onIsCellColourEnabledChanged = (newValue: boolean) => {
    updateSeries({
      ...series,
      cellColourConfig: newValue
        ? { mode: 'manual', direction: 'red-to-green', thresholds: [] }
        : undefined,
    });
  };
  const onCellColourConfigChanged = useCallback(
    (newConfig: FleetOps.BonusScoreColourConfig) => {
      updateSeries({ ...series, cellColourConfig: newConfig });
    },
    [series, updateSeries],
  );
  const onSmartCardComparison = (newType?: MatrixCellType) => {
    updateSeries({ ...series, smartCardComparison: newType });
  };

  useEffect(() => {
    if (
      !isMatrix &&
      draftChart !== undefined &&
      (!!series.matrixCellType ||
        !!series.matrixShowComparisonValue ||
        !!series.cellColourConfig)
    ) {
      updateSeries({
        ...series,
        matrixCellType: undefined,
        matrixShowComparisonValue: undefined,
        cellColourConfig: undefined,
      });
    }
  }, [draftChart, isMatrix, series, updateSeries]);

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const updateDisplayName = useCallback(
    _.debounce((newName: string) => {
      updateSeries({ ...series, displayName: newName });
    }, 500),
    [series, updateSeries],
  );

  const onDisplayNameChanged = (event: ChangeEvent<HTMLInputElement>) => {
    setDisplayName(event.target.value);
    updateDisplayName(event.target.value);
  };

  const onNewMetricSelected = useCallback(
    (newMetric: Metrics.Metric | undefined) => {
      if (!newMetric) {
        removeSeries(series);
        return;
      }

      const newS = {
        ...series,
        metricId: newMetric.id,
        displayName: newMetric.name,
      };

      setDisplayName(newMetric.name);
      updateSeries(newS);
    },
    [series, updateSeries, removeSeries],
  );

  return (
    <ItemWrapper>
      <Label>Metric</Label>
      <InputWrapper>
        <Row spaceBetween>
          <Inputs.MetricPicker
            setMetricId={window.tokenFunction}
            onMetricSelected={onNewMetricSelected}
            metricId={series.metricId}
            metricIdsToConstrainBy={
              numSeries === 1 ? window.emptyArray : metricIdsToConstrainBy
            }
          />
          <div>
            <Button
              onClick={() => removeSeries(series)}
              icon="cross"
              type="Secondary"
              size="Small"
            />
          </div>
        </Row>
      </InputWrapper>

      {hasQueryOverrides(series.queryOverrides) && (
        <InputWrapper>
          <Label>Own query</Label>
          <Typography.Body type="Body 12">
            {`${describeQueryOverrides({
              overrides: series.queryOverrides,
            })}.${canOpenStudio ? ' Edit it in Studio.' : ''}`}
          </Typography.Body>
        </InputWrapper>
      )}

      <InputWrapper>
        <Label>Display Name</Label>
        <Inputs.TextInput
          value={displayName}
          onChange={onDisplayNameChanged}
          inputSize="Small"
          width="100%"
        />
      </InputWrapper>

      {(isBar || isTimeSeries || isCombo) && (
        <YAxisConfig series={series} updateSeries={updateSeries} />
      )}

      {isMatrix && (
        <InputWrapper>
          <Label>Is status flag</Label>
          <Inputs.Toggle
            value={!!series.isStatusFlag}
            onChange={onIsStatusFlagChanged}
          />
        </InputWrapper>
      )}
      {isSmartCard && (
        <InputWrapper>
          <Label>Cell comparison</Label>
          <Inputs.Dropdown
            selectedLabel={series.smartCardComparison}
            options={['delta', 'percentDelta', 'both', 'none'].map((o) => ({
              label: o ? o : 'None',
              onSelected: () =>
                onSmartCardComparison(o as MatrixCellType | undefined),
            }))}
          />
        </InputWrapper>
      )}
      {isMultipleYAxisChart && (
        <React.Fragment>
          <InputWrapper>
            <Label>Separate Y axis</Label>
            <Inputs.Toggle
              value={!!series.plotOnSeparateAxis}
              onChange={onPlotOnSeparateYAxisChange}
            />
          </InputWrapper>

          <InputWrapper>
            <Label>Opposite</Label>
            <Inputs.Toggle
              value={!!series.opposite}
              onChange={onOppositeChange}
            />
          </InputWrapper>

          <InputWrapper>
            <Label>Plot average</Label>
            <Inputs.Toggle
              value={!!series.plotAverage}
              onChange={onPlotAverageChange}
            />
          </InputWrapper>

          <InputWrapper>
            <Label>Plot trend</Label>
            <Inputs.Toggle
              value={!!series.plotTrend}
              onChange={onPlotTrendChange}
            />
          </InputWrapper>

          <InputWrapper>
            <Label>Type</Label>
            <Inputs.Dropdown
              selectedLabel={series.type}
              options={['column', 'bar', 'line'].map((o) => ({
                label: toSentenceCase(o),
                onSelected: () => {
                  onTypeChange(o);
                },
              }))}
            />
          </InputWrapper>
        </React.Fragment>
      )}
      {isMatrix && (
        <React.Fragment>
          <InputWrapper>
            <Label>Cell comparison</Label>
            <Inputs.Dropdown
              selectedLabel={series.matrixCellType}
              options={['delta', 'percentDelta', 'both', undefined].map(
                (o) => ({
                  label: o ? o : 'None',
                  onSelected: () => {
                    onMatrixCellTypeChanged(o as MatrixCellType | undefined);
                  },
                }),
              )}
            />
          </InputWrapper>
        </React.Fragment>
      )}
      {is1DMatrix && hasCellComparison && (
        <InputWrapper>
          <Label>Show comparison value</Label>
          <Inputs.Toggle
            value={!!series.matrixShowComparisonValue}
            onChange={onMatrixShowComparisonValueChanged}
          />
        </InputWrapper>
      )}
      {isCellColourAvailable && (
        <InputWrapper>
          <Label>Colour by thresholds</Label>
          <Inputs.Toggle
            value={!!series.cellColourConfig}
            onChange={onIsCellColourEnabledChanged}
          />
          {series.cellColourConfig && (
            <div style={{ marginTop: 8 }}>
              <ThresholdColourInput
                colourConfig={series.cellColourConfig}
                onChange={onCellColourConfigChanged}
              />
            </div>
          )}
        </InputWrapper>
      )}
    </ItemWrapper>
  );
};

const OrderItemName = ({ series }: { series: V5ChartDefinitionSeries }) => {
  const { metricOptions } = useContext(MetricOptionsContext);
  const label = (() => {
    if (series.displayName) {
      return series.displayName;
    }
    const selected = metricOptions.find((m) => m.id === series.metricId);
    if (selected) {
      return selected.name;
    }
    return '';
  })();

  return <Typography.Body type="Body 12">{label}</Typography.Body>;
};

const OrderItemRow = ({
  series,
  updateSeries,
  isMatrix,
}: {
  series: V5ChartDefinitionSeries;
  updateSeries: (s: V5ChartDefinitionSeries) => void;
  isMatrix: boolean;
}) => {
  const onTotalToggled = useCallback(
    (newValue: boolean) => {
      updateSeries({ ...series, isTotalEnabled: newValue });
    },
    [series, updateSeries],
  );

  return (
    <OrderItem>
      <Row centerAlign>
        <DragHandle noMargin />
        <OrderItemName series={series} />
      </Row>
      {isMatrix && (
        <Inputs.Checkbox
          isChecked={series.isTotalEnabled !== false}
          onToggled={onTotalToggled}
        />
      )}
    </OrderItem>
  );
};

const AddSeriesButton = ({
  onAddSeries,
  metricIdsToConstrainBy,
}: {
  onAddSeries: (metric: Metrics.Metric) => void;
  metricIdsToConstrainBy: string[];
}) => {
  const { isOpen, open, close } = usePopup();
  const {
    isOpen: isSingleUseOpen,
    open: openSingleUse,
    close: closeSingleUse,
  } = usePopup();
  const { trackEvent } = useContext(AnalyticsContext);

  const onClick = useCallback(() => {
    trackEvent('Gadget Builder - Add Metric Clicked');
    open();
  }, [open, trackEvent]);

  const onSingleUseClicked = useCallback(() => {
    trackEvent('Gadget builder - Create Single use metric clicked');
    openSingleUse();
  }, [openSingleUse, trackEvent]);

  const onSingleUseMetricSaved = useCallback(
    (metric: Metrics.Metric) => {
      trackEvent('Single use metric - Created');
      onAddSeries(metric);
      closeSingleUse();
    },
    [closeSingleUse, onAddSeries, trackEvent],
  );

  return (
    <>
      <Row style={{ flexWrap: 'nowrap' }}>
        <div style={{ marginRight: 8, whiteSpace: 'nowrap' }}>
          <Button
            onClick={onClick}
            label="Add Metric"
            type="Primary"
            size="Small"
          />
        </div>
        <PermissionGates.Has
          requiredPermission={
            PERMISSIONS.DATA_MANAGEMENT.SINGLE_USE_METRICS_CONFIG
          }
        >
          <div style={{ whiteSpace: 'nowrap' }}>
            <Button
              onClick={onSingleUseClicked}
              label="Create Single-use Metric"
              type="Tertiary"
              size="Small"
            />
          </div>
        </PermissionGates.Has>
      </Row>
      <MetricPickerPopup
        isOpen={isOpen}
        close={close}
        setSelectedMetric={onAddSeries}
        metricIdsToConstrainBy={metricIdsToConstrainBy}
      />
      <SingleUseMetricBuilderPopup
        close={closeSingleUse}
        onSingleUseMetricSaved={onSingleUseMetricSaved}
        isOpen={isSingleUseOpen}
        cancelButtonText="Back to Gadget Settings"
      />
    </>
  );
};

const SeriesInput = ({
  isAddButtonVisible,
  series,
  onAddSeries,
  updateSeries,
  removeSeries,
  isMultipleYAxisChart,
  isMatrix,
  onOrderChanged,
  metricIdsToConstrainBy,
}: {
  isAddButtonVisible: boolean;
  series: V5ChartDefinitionSeries[];
  onAddSeries: (metric: Metrics.Metric) => void;
  updateSeries: (s: V5ChartDefinitionSeries) => void;
  removeSeries: (s: V5ChartDefinitionSeries) => void;
  isMultipleYAxisChart: boolean;
  isMatrix: boolean;
  onOrderChanged: (newOrder: V5ChartDefinitionSeries[]) => void;
  metricIdsToConstrainBy: string[];
}) => (
  <div>
    {series.length > 1 && (
      <ItemWrapper>
        <Row spaceBetween centerAlign style={{ marginBottom: 8 }}>
          <Typography.Body type="Label">Order</Typography.Body>
          {isMatrix && (
            <Typography.Body type="Label">Show Total</Typography.Body>
          )}
        </Row>
        <DragAndDropList
          items={series}
          onOrderChanged={onOrderChanged}
          droppableId="seriesInput"
          renderItem={(s) => (
            <OrderItemRow
              series={s}
              updateSeries={updateSeries}
              isMatrix={isMatrix}
            />
          )}
        />
      </ItemWrapper>
    )}
    {series.map((s) => (
      <Item
        key={s.id}
        series={s}
        metricIdsToConstrainBy={metricIdsToConstrainBy}
        updateSeries={updateSeries}
        removeSeries={removeSeries}
        isMultipleYAxisChart={isMultipleYAxisChart}
        numSeries={series.length}
      />
    ))}
    {isAddButtonVisible && (
      <Row centerAlign>
        <AddSeriesButton
          onAddSeries={onAddSeries}
          metricIdsToConstrainBy={metricIdsToConstrainBy}
        />
      </Row>
    )}
  </div>
);

export default SeriesInput;
